import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './TeamDetailPage.css';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { SUPABASE_ANON_KEY, FUNCTIONS_URL } from '../services/supabaseConfig';
import { getTeamHistory, TeamSeasonRecord } from '../services/SeasonHistoryService';
import BoxScore from '../components/BoxScore';
import { LEAGUE_TYPE, QL_ARRAY, TRAD_ARRAY } from '../constants/leagueTypes';
import { numberToGrade, getGradeColor } from '../utils/grades';

interface TeamFromDB {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
  tier: number;
  division: string;
  wins: number;
  losses: number;
  is_ai?: boolean;
  owner_id?: string;
  league_id?: string;
  rotation_order?: string[];
  batting_order?: string[];
  depth_chart?: Record<string, string[]>;
}

interface PlayerFromDB {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  overall: number;
  is_hitter: boolean;
  power: number;
  contact: number;
  speed: number;
  velocity: number;
  control: number;
  movement: number;
  user_id?: string;
}

interface StandingFromDB {
  team_id: string;
  wins: number;
  losses: number;
  runs_scored: number;
  runs_allowed: number;
}

interface TeamDetailPageProps {
  teamId?: string;
  onNavigate?: (page: string) => void;
  currentUserId?: string;
}

const TeamDetailPage: React.FC<TeamDetailPageProps> = ({ teamId: propTeamId, onNavigate, currentUserId }) => {
  const params = useParams();
  const teamId = propTeamId || params.teamId;
  const [team, setTeam] = useState<TeamFromDB | null>(null);
  const [players, setPlayers] = useState<PlayerFromDB[]>([]);
  const [standing, setStanding] = useState<StandingFromDB | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'roster' | 'stats' | 'schedule' | 'history' | 'draft' | 'lineup' | 'free-agency'>(() => {
    const hash = window.location.hash?.slice(1);
    const valid = ['roster','stats','schedule','history','draft','lineup','free-agency'];
    return (valid.includes(hash) ? hash : 'roster') as any;
  });
  const [leagueStatus, setLeagueStatus] = useState<string | null>(null);
  const [leagueSetupEnd, setLeagueSetupEnd] = useState<string | null>(null);
  const [lineupTimer, setLineupTimer] = useState<number>(0);
  const [teamHistory, setTeamHistory] = useState<TeamSeasonRecord[]>([]);
  const [hitterSort, setHitterSort] = useState<{ col: string; asc: boolean }>({ col: 'position', asc: true });
  const [pitcherSort, setPitcherSort] = useState<{ col: string; asc: boolean }>({ col: 'overall', asc: false });
  const [ownerName, setOwnerName] = useState<string | null>(null);
  const [isQuickLeague, setIsQuickLeague] = useState(false);
  const [userPlayers, setUserPlayers] = useState<PlayerFromDB[]>([]);
  const [boxscoreGame, setBoxscoreGame] = useState<string | null>(null);
  const [boxscoreLeague, setBoxscoreLeague] = useState<string>('');
  const [faBudget, setFaBudget] = useState<number>(1000000);
  // Team edit state
  const [editingTeam, setEditingTeam] = useState(false);
  const [editName, setEditName] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editAbbr, setEditAbbr] = useState('');
  const [savingTeam, setSavingTeam] = useState(false);

  useEffect(() => {
    if (teamId) loadData();
  }, [teamId]);

  // Sync active tab to URL hash so back button works
  useEffect(() => {
    if (window.location.hash?.slice(1) !== activeTab) {
      window.history.replaceState({}, '', `#${activeTab}`);
    }
  }, [activeTab]);

  // Lineup countdown timer for quick leagues in free_agency
  useEffect(() => {
    if (!leagueSetupEnd || leagueStatus !== 'free_agency') { setLineupTimer(0); return; }
    const end = new Date(leagueSetupEnd).getTime();
    const update = () => {
      const remaining = Math.max(0, Math.floor((end - Date.now()) / 1000));
      setLineupTimer(remaining);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [leagueSetupEnd, leagueStatus]);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUserId) return;
    if (file.size > 512000) { alert('File too large. Max 500KB.'); return; }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { alert('Use JPG, PNG, or WebP.'); return; }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', currentUserId);
    formData.append('targetType', 'team');
    formData.append('targetId', propTeamId!);
    try {
      const res = await fetch(`${FUNCTIONS_URL}/upload-avatar`, {
        method: 'POST', body: formData,
        headers: { 'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}` },
      });
      const data = await res.json();
      if (data.url) {
        setTeam({ ...team!, logo_url: data.url } as any);
      } else { alert(data.error || 'Upload failed'); }
    } catch { alert('Upload failed'); }
  };

  async function loadData() {
    setLoading(true);
    
    if (isSupabaseConfigured() && supabase && teamId) {
      const { data: teamData } = await supabase
        .from('teams')
        .select('*')
        .eq('id', teamId)
        .single();
      
      if (teamData) {
        // If the owner is viewing, redirect to owner office with team pre-selected
        const userId = currentUserId || localStorage.getItem('userId');
        if (userId && teamData.owner_id === userId) {
          onNavigate?.(`/my-teams?team=${teamId}`);
          setLoading(false);
          return;
        }
        setTeam(teamData as TeamFromDB);
      }
      
      // Load league status for quick league teams
      if (teamData?.league_id) {
        const { data: leagueData } = await supabase.from('leagues').select('status, setup_window_end, fa_budget, league_type').eq('id', teamData.league_id).single();
        if (leagueData) {
          setLeagueStatus(leagueData.status);
          setLeagueSetupEnd(leagueData.setup_window_end);
          const quickTypes: readonly string[] = [LEAGUE_TYPE.QUICK];
          setIsQuickLeague(quickTypes.includes(leagueData.league_type || ''));
        }
        // Load team FA budget
        if (teamData.fa_budget !== undefined) setFaBudget(teamData.fa_budget);
      }
      
      // Load owner info if team has an owner_id that's not the team ID itself
      if (teamData?.owner_id && teamData.owner_id !== teamData.id) {
        const { data: ownerData } = await supabase
          .from('users')
          .select('username')
          .eq('id', teamData.owner_id)
          .single();
        if (ownerData?.username) setOwnerName(ownerData.username);
      }
      
      // Load user's free agents for contract request dropdown
      if ((teamData as any)?.is_ai && !(teamData as any)?.owner_id) {
        const userId = localStorage.getItem('userId');
        if (userId) {
          const { data: myPlayers } = await supabase
            .from('players')
            .select('id, first_name, last_name, position, overall, is_hitter, power, contact, speed, discipline, batting_attr, glove_attr, range_attr, velocity, control, movement')
            .eq('user_id', userId)
            .is('team_id', null);
          if (myPlayers) setUserPlayers(myPlayers as PlayerFromDB[]);
        }
      }
      
      const { data: playerData } = await supabase
        .from('players')
        .select('*')
        .eq('team_id', teamId)
        .eq('retired', false)
        .order('overall', { ascending: false });
      
      if (playerData) setPlayers(playerData as PlayerFromDB[]);
      
      const { data: standingData } = await supabase
        .from('league_standings')
        .select('*')
        .eq('team_id', teamId)
        .order('season_year', { ascending: false })
        .limit(1)
        .single();
      
      if (standingData) setStanding(standingData as StandingFromDB);

      // Load team history
      const history = await getTeamHistory(teamId);
      setTeamHistory(history);
    }
    
    setLoading(false);
  }

  const handleSaveTeam = async () => {
    if (!team || !supabase) return;
    setSavingTeam(true);
    const name = editName.trim() || team.name;
    const city = editCity.trim() || team.city;
    const abbreviation = editAbbr.trim() || team.abbreviation;
    const { error } = await supabase.from('teams').update({
      name, city, abbreviation,
    }).eq('id', team.id);
    if (error) { alert('Failed to save: ' + error.message); }
    else {
      setTeam({ ...team, name, city, abbreviation } as any);
      setEditingTeam(false);
    }
    setSavingTeam(false);
  };

  const openEditTeam = () => {
    if (!team) return;
    setEditName(team.name);
    setEditCity(team.city);
    setEditAbbr(team.abbreviation || '');
    setEditingTeam(true);
  };



  const getTierName = (tier: number): string => {
    const names: Record<number, string> = { 1: 'Elite', 2: 'Platinum', 3: 'Gold', 4: 'Silver', 5: 'Bronze' };
    return names[tier] || `Tier ${tier}`;
  };

  const getTierColor = (tier: number): string => {
    const colors: Record<number, string> = { 1: '#b9f2ff', 2: '#e5e4e2', 3: '#ffd700', 4: '#c0c0c0', 5: '#cd7f32' };
    return colors[tier] || '#ccc';
  };

  if (loading) {
    return (
      <div className="team-detail-page">
        <div className="loading-container">
          <div className="spinner" />
          <p>Loading team...</p>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="team-detail-page">
        <div className="not-found">
          <h2>Team not found</h2>
          <p>The team you're looking for doesn't exist.</p>
          <button onClick={() => onNavigate?.('leagues')}>Back to Leagues</button>
        </div>
      </div>
    );
  }

  const positionOrder: Record<string, number> = { 'C': 1, '1B': 2, '2B': 3, 'SS': 4, '3B': 5, 'LF': 6, 'CF': 7, 'RF': 8, 'DH': 9 };
  // All pitchers are just "P" - sort by overall instead of position type
  const pitcherPosOrder: Record<string, number> = { 'P': 1 };

  const sortPlayers = (list: PlayerFromDB[], sort: { col: string; asc: boolean }, isPitcher: boolean) => {
    return [...list].sort((a, b) => {
      let valA: any, valB: any;
      if (sort.col === 'position') {
        const order = isPitcher ? pitcherPosOrder : positionOrder;
        valA = order[a.position] || 99;
        valB = order[b.position] || 99;
      } else if (sort.col === 'name') {
        valA = `${a.last_name} ${a.first_name}`.toLowerCase();
        valB = `${b.last_name} ${b.first_name}`.toLowerCase();
      } else if (sort.col === 'overall') {
        valA = a.overall || 0;
        valB = b.overall || 0;
      } else if (sort.col === 'power') {
        valA = a.power || 0;
        valB = b.power || 0;
      } else if (sort.col === 'contact') {
        valA = a.contact || 0;
        valB = b.contact || 0;
      } else if (sort.col === 'speed') {
        valA = a.speed || 0;
        valB = b.speed || 0;
      } else if (sort.col === 'velocity') {
        valA = a.velocity || 0;
        valB = b.velocity || 0;
      } else if (sort.col === 'control') {
        valA = a.control || 0;
        valB = b.control || 0;
      } else if (sort.col === 'movement') {
        valA = a.movement || 0;
        valB = b.movement || 0;
      } else {
        return 0;
      }
      if (valA < valB) return sort.asc ? -1 : 1;
      if (valA > valB) return sort.asc ? 1 : -1;
      return 0;
    });
  };

  const hitters = sortPlayers(players.filter(p => p.is_hitter), hitterSort, false);
  const pitchers = sortPlayers(players.filter(p => !p.is_hitter), pitcherSort, true);
  const pitcherIds = new Set(pitchers.map(p => p.id));

  const handleHitterSort = (col: string) => {
    setHitterSort(prev => ({ col, asc: prev.col === col ? !prev.asc : true }));
  };
  const handlePitcherSort = (col: string) => {
    setPitcherSort(prev => ({ col, asc: prev.col === col ? !prev.asc : true }));
  };
  const getSortArrow = (col: string, sort: { col: string; asc: boolean }) => 
    sort.col === col ? (sort.asc ? ' ▲' : ' ▼') : '';

  return (
    <div className="team-detail-page">
      {/* Header */}
      <div className="team-header">
        <div className="team-header-row">
          <div className="team-logo-area">
            {(team as any).logo_url ? (
              <img src={(team as any).logo_url} alt="Team logo" className="team-logo-img" />
            ) : (
              <div className="team-logo-placeholder">{team.abbreviation || team.name.slice(0, 2)}</div>
            )}
            {currentUserId && team.owner_id === currentUserId && (
              <label className="logo-upload-btn" title="Upload logo">
                📷
                <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleLogoUpload} />
              </label>
            )}
          </div>
          <div>
            {editingTeam ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input value={editCity} onChange={e => setEditCity(e.target.value)} placeholder="City" style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #30363d', background: '#0d1117', color: '#f0ede6', fontSize: 14, width: 120 }} />
                  <input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Team name" style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #30363d', background: '#0d1117', color: '#f0ede6', fontSize: 14, width: 180 }} />
                  <input value={editAbbr} onChange={e => setEditAbbr(e.target.value)} placeholder="Abbr" maxLength={4} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #30363d', background: '#0d1117', color: '#f0ede6', fontSize: 14, width: 64 }} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={handleSaveTeam} disabled={savingTeam} style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: '#22c55e', color: '#000', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>{savingTeam ? 'Saving...' : '✅ Save'}</button>
                  <button onClick={() => setEditingTeam(false)} style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #30363d', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                </div>
              </div>
            ) : (
            <div className="team-title">
              <h1>{team.name}</h1>
              <span className="team-tier" style={{ background: getTierColor(team.tier) }}>
                {getTierName(team.tier)}
              </span>
              {currentUserId && team.owner_id === currentUserId && (
                <button onClick={openEditTeam} style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 4, border: '1px solid #30363d', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>✏️ Edit</button>
              )}
            </div>
            )}
            <div className="team-record">
              <span className="record">{standing?.wins || 0} - {standing?.losses || 0}</span>
              <span className="division">{team.division || 'East'} Division</span>
            </div>
            {ownerName && (
              <div className="team-owner">
                <span className="owner-label">Owner:</span>
                <span className="owner-name">{ownerName}</span>
              </div>
            )}
          </div>
        </div>
        <button className="back-btn" style={{background:'#374151',color:'#fff'}} onClick={() => onNavigate?.(`/league/${team.league_id}`)}>← Back to League</button>
      </div>

      {/* AI Team — Request Contract */}
      {team.is_ai && !team.owner_id && (
        <div className="ai-contract-bar">
          <span className="ai-label">🤖 AI Team — Sign Your Player</span>
          <select className="ai-player-select" id="request-player-select">
            <option value="">Select your player...</option>
            {userPlayers.map(p => (
              <option key={p.id} value={p.id}>
                {p.first_name} {p.last_name} ({p.position}, OVR {numberToGrade(p.overall)})
              </option>
            ))}
          </select>
          <button className="request-contract-btn" onClick={async () => {
            const select = document.getElementById('request-player-select') as HTMLSelectElement;
            const playerId = select?.value;
            if (!playerId) { alert('Select a player first'); return; }
            const { requestAIContract } = await import('../services/AIContractService');
            const result = await requestAIContract(playerId, team!.id);
            alert(result.message);
            if (result.success) loadData();
          }}>
            ✍️ Sign Player
          </button>
        </div>
      )}

      {/* Quick league banners */}
      {leagueStatus === 'drafting' && (
        <div style={{padding:'12px 16px',background:'rgba(59,130,246,0.15)',borderRadius:8,marginBottom:12,textAlign:'center',border:'1px solid #3b82f640'}}>
          <span style={{color:'#60a5fa',fontWeight:600}}>📋 Draft in progress</span>
          <button onClick={() => team?.league_id && onNavigate?.(`/league/${team.league_id}/draft-sheet`)}
            style={{marginLeft:12,padding:'4px 12px',borderRadius:6,border:'none',background:'#3b82f6',color:'#fff',cursor:'pointer',fontSize:'0.85rem'}}>
            View Draft Sheet
          </button>
        </div>
      )}
      {leagueStatus === 'free_agency' && lineupTimer > 0 && (
        <div style={{padding:'12px 16px',background:'rgba(34,197,94,0.1)',borderRadius:8,marginBottom:12,textAlign:'center',border:'1px solid #22c55e40'}}>
          <span style={{color:'#4ade80',fontWeight:600}}>🤝 Free Agency Open — Set your lineups!</span>
          <span style={{marginLeft:12,color:'#fbbf24',fontFamily:'monospace',fontWeight:700}}>{Math.floor(lineupTimer/60)}:{(lineupTimer%60).toString().padStart(2,'0')}</span>
          <button onClick={() => team?.league_id && onNavigate?.(`/league/${team.league_id}`)}
            style={{marginLeft:12,padding:'4px 12px',borderRadius:6,border:'none',background:'#22c55e',color:'#fff',cursor:'pointer',fontSize:'0.85rem'}}>
            View League
          </button>
          <button onClick={() => team?.league_id && onNavigate?.(`/league/${team.league_id}/free-agency`)}
            style={{marginLeft:8,padding:'4px 12px',borderRadius:6,border:'none',background:'#3b82f6',color:'#fff',cursor:'pointer',fontSize:'0.85rem'}}>
            Free Agents
          </button>
        </div>
      )}
      {leagueStatus === 'free_agency' && (
        <div style={{padding:'8px 16px',background:'rgba(59,130,246,0.08)',borderRadius:8,marginBottom:12,textAlign:'center',fontSize:'0.85rem',color:'#60a5fa'}}>
          💰 FA Budget: ${(faBudget || 1000000).toLocaleString()}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${activeTab === 'roster' ? 'active' : ''}`} onClick={() => setActiveTab('roster')}>
          👥 Roster ({players.length})
        </button>
        <button className={`tab ${activeTab === 'stats' ? 'active' : ''}`} onClick={() => setActiveTab('stats')}>
          📊 Stats
        </button>
        <button className={`tab ${activeTab === 'schedule' ? 'active' : ''}`} onClick={() => setActiveTab('schedule')}>
          📅 Schedule
        </button>
        <button className={`tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')} style={isQuickLeague ? { display: 'none' } : {}}>
          📜 History
        </button>
        <button className={`tab ${activeTab === 'lineup' ? 'active' : ''}`} onClick={() => setActiveTab('lineup')}>
          ⚾ Lineup & Strategy
        </button>
        {team?.league_id && leagueStatus === 'free_agency' && (
          <button className={`tab ${activeTab === 'free-agency' ? 'active' : ''}`} onClick={() => setActiveTab('free-agency')}>
            🤝 Free Agency
          </button>
        )}
        {team?.league_id && (leagueStatus === 'drafting' || leagueStatus === 'free_agency') && (
          <button className={`tab ${activeTab === 'draft' ? 'active' : ''}`} onClick={() => setActiveTab('draft')}>
            📋 Draft
          </button>
        )}
      </div>

      {/* Roster Tab */}
      {activeTab === 'roster' && (
        <div className="tab-content">
          <div className="roster-section">
            <h3>🏏 Hitters ({hitters.length})</h3>
            <table className="roster-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleHitterSort('name')}>Name{getSortArrow("name", hitterSort)}</th>
                  <th className="sortable" onClick={() => handleHitterSort('position')}>Pos{getSortArrow("position", hitterSort)}</th>
                  <th className="sortable" onClick={() => handleHitterSort('overall')}>OVR{getSortArrow("overall", hitterSort)}</th>
                  <th className="sortable" onClick={() => handleHitterSort('power')}>POW{getSortArrow("power", hitterSort)}</th>
                  <th className="sortable" onClick={() => handleHitterSort('contact')}>CON{getSortArrow("contact", hitterSort)}</th>
                  <th className="sortable" onClick={() => handleHitterSort('speed')}>SPD{getSortArrow("speed", hitterSort)}</th>
                </tr>
              </thead>
              <tbody>
                {hitters.map(player => (
                  <tr key={player.id} className="roster-row" onClick={() => onNavigate?.(`/player/${player.id}`)}>
                    <td className="player-name-cell">
                      {player.first_name} {player.last_name}
                      {!player.user_id && <span className="roster-buy-tag" title="Buy rights to this player">🏷️</span>}
                    </td>
                    <td>{player.position}</td>
                    <td className="grade-cell">{numberToGrade(player.overall)}</td>
                    <td>{numberToGrade(player.power || 50)}</td>
                    <td>{numberToGrade(player.contact || 50)}</td>
                    <td>{numberToGrade(player.speed || 50)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Starting Rotation */}
          {(() => {
            const ro = team.rotation_order;
            if (!ro || ro.length === 0) return null;
            return (
            <div className="roster-section">
              <h3>🔄 Starting Rotation</h3>
              <div className="rotation-row">
                {ro.filter((pid: string) => pitcherIds.has(pid)).map((pid: string, idx: number) => {
                  const sp = players.find(p => p.id === pid);
                  if (!sp) return null;
                  return (
                    <div key={pid} className="rotation-slot" onClick={() => onNavigate?.(`/player/${pid}`)}>
                      <span className="rotation-number">{idx + 1}</span>
                      <span className="rotation-name">{sp.first_name} {sp.last_name}</span>
                      <span className="rotation-ovr">{numberToGrade(sp.overall)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="rotation-legend">Bullpen: {ro.filter((pid: string) => !pitcherIds.has(pid) || ro.indexOf(pid) >= 5).length > 0 ? 'remaining pitchers' : 'remaining pitchers after top 5'}</div>
            </div>
          );
          })()}

          <div className="roster-section">
            <h3>🔥 Pitchers ({pitchers.length})</h3>
            <table className="roster-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handlePitcherSort('name')}>Name{getSortArrow("name", pitcherSort)}</th>
                  <th className="sortable" onClick={() => handlePitcherSort('position')}>Pos{getSortArrow("position", pitcherSort)}</th>
                  <th className="sortable" onClick={() => handlePitcherSort('overall')}>OVR{getSortArrow("overall", pitcherSort)}</th>
                  <th className="sortable" onClick={() => handlePitcherSort('velocity')}>VEL{getSortArrow("velocity", pitcherSort)}</th>
                  <th className="sortable" onClick={() => handlePitcherSort('control')}>CTL{getSortArrow("control", pitcherSort)}</th>
                  <th className="sortable" onClick={() => handlePitcherSort('movement')}>MOV{getSortArrow("movement", pitcherSort)}</th>
                </tr>
              </thead>
              <tbody>
                {pitchers.map(player => (
                  <tr key={player.id} className="roster-row" onClick={() => onNavigate?.(`/player/${player.id}`)}>
                    <td className="player-name-cell">
                      {player.first_name} {player.last_name}
                      {!player.user_id && <span className="roster-buy-tag" title="Buy rights to this player">🏷️</span>}
                    </td>
                    <td>P</td>
                    <td className="grade-cell">{numberToGrade(player.overall)}</td>
                    <td>{numberToGrade(player.velocity || 50)}</td>
                    <td>{numberToGrade(player.control || 50)}</td>
                    <td>{numberToGrade(player.movement || 50)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <div className="tab-content">
          {/* Team Summary */}
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-label">Wins</span>
              <span className="stat-value wins">{standing?.wins || 0}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Losses</span>
              <span className="stat-value losses">{standing?.losses || 0}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Runs Scored</span>
              <span className="stat-value">{standing?.runs_scored || 0}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Runs Allowed</span>
              <span className="stat-value">{standing?.runs_allowed || 0}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Run Diff</span>
              <span className="stat-value diff">{(standing?.runs_scored || 0) - (standing?.runs_allowed || 0)}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Win %</span>
              <span className="stat-value">
                {standing && standing.wins + standing.losses > 0
                  ? (standing.wins / (standing.wins + standing.losses)).toFixed(3).replace(/^0/, '')
                  : '.000'}
              </span>
            </div>
          </div>

          <TeamStatsTables teamId={teamId} players={players} onNavigate={onNavigate} leagueId={team?.league_id} />
        </div>
      )}

      {/* Schedule Tab */}
      {activeTab === 'schedule' && (
        <div className="tab-content">
          <TeamSchedule teamId={teamId} team={team} onNavigate={onNavigate} onViewBoxscore={({gameId, leagueId}) => { setBoxscoreGame(gameId); setBoxscoreLeague(leagueId || team?.league_id || ''); }} />
        </div>
      )}

      {activeTab === 'lineup' && (
        <LineupStrategyTab teamId={teamId!} team={team} players={players} currentUserId={currentUserId} />
      )}

      {activeTab === 'free-agency' && team?.league_id && leagueStatus === 'free_agency' && (
        <FreeAgencyTab leagueId={team.league_id} teamId={teamId!} currentUserId={currentUserId} />
      )}

      {activeTab === 'draft' && team?.league_id && (
        <div className="tab-content">
          <DraftPriorityView leagueId={team.league_id} teamId={teamId!} onNavigate={onNavigate} />
        </div>
      )}

      {activeTab === 'history' && (
        <div className="tab-content">
          {teamHistory.length > 0 ? (
            <div className="history-table-wrap">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Season</th>
                    <th>W</th>
                    <th>L</th>
                    <th>PCT</th>
                    <th>RS</th>
                    <th>RA</th>
                    <th>DIFF</th>
                    <th>Div</th>
                    <th>Finish</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {teamHistory.map((h) => {
                    const total = h.wins + h.losses;
                    const pct = total > 0 ? (h.wins / total).toFixed(3) : '.000';
                    const diff = h.run_diff || (h.runs_scored - h.runs_allowed);
                    const resultLabel: Record<string, string> = {
                      'pending': '—',
                      'missed': 'Missed Playoffs',
                      'lost_div': 'Lost Div Series',
                      'lost_conf': 'Lost Conf Series',
                      'lost_ws': 'Lost World Series',
                      'won_ws': '🏆 World Series Champs',
                    };
                    return (
                      <tr key={h.id} className={h.championship ? 'champion-row' : ''}>
                        <td className="season-year">{h.season_year}</td>
                        <td className="wins">{h.wins}</td>
                        <td className="losses">{h.losses}</td>
                        <td>{pct}</td>
                        <td>{h.runs_scored}</td>
                        <td>{h.runs_allowed}</td>
                        <td className={diff >= 0 ? 'positive' : 'negative'}>{diff > 0 ? '+' : ''}{diff}</td>
                        <td>{h.conference} {h.division}</td>
                        <td>#{h.final_rank}</td>
                        <td className="result">{resultLabel[h.playoff_result] || h.playoff_result}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">
              <p>No season history yet.</p>
              <p>History will appear after the first completed season.</p>
            </div>
          )}
        </div>
      )}

      {/* BoxScore Modal */}
      {boxscoreGame && (
        <BoxScore gameId={boxscoreGame} leagueId={boxscoreLeague || ''} onClose={() => setBoxscoreGame(null)} />
      )}
    </div>
  );
};

// Team schedule component
const TeamSchedule: React.FC<{ teamId?: string; team: TeamFromDB | null; onNavigate?: (page: string) => void; onViewBoxscore?: (opts: {gameId: string; leagueId: string}) => void }> = ({ teamId, team, onNavigate, onViewBoxscore }) => {
  const [games, setGames] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadSchedule(); }, [teamId]);

  async function loadSchedule() {
    setLoading(true);
    if (!isSupabaseConfigured() || !supabase || !teamId) { setLoading(false); return; }

    const { data } = await supabase
      .from('league_schedule')
      .select('*')
      .or(`home_team_id.eq.${teamId},away_team_id.eq.${teamId}`)
      .order('game_day', { ascending: false })
      .limit(200);

    // Load team names separately
    if (data && data.length > 0) {
      const teamIds = new Set<string>();
      data.forEach(g => { teamIds.add(g.home_team_id); teamIds.add(g.away_team_id); });
      const { data: teams } = await supabase.from('teams').select('id, city, name').in('id', Array.from(teamIds));
      if (teams) {
        const teamMap = new Map(teams.map(t => [t.id, t]));
        setGames(data.map(g => ({
          ...g,
          home: teamMap.get(g.home_team_id) || null,
          away: teamMap.get(g.away_team_id) || null
        })));
      } else {
        setGames(data);
      }
    } else {
      setGames([]);
    }
    setLoading(false);
  }

  if (loading) return <p className="loading-text">Loading schedule...</p>;
  if (games.length === 0) return <div className="no-data"><p>No games scheduled.</p></div>;

  // Group by game day
  const grouped: Record<number, any[]> = {};
  games.forEach(g => {
    if (!grouped[g.game_day]) grouped[g.game_day] = [];
    grouped[g.game_day].push(g);
  });

  const dayToDate = (day: number) => {
    const d = new Date(2026, 3, 3 + day - 1);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const record: { w: number; l: number } = { w: 0, l: 0 };

  return (
    <div className="team-schedule">
      {Object.entries(grouped).sort(([a], [b]) => Number(a) - Number(b)).map(([day, dayGames]) => (
        <div key={day} className="schedule-day">
          <h4 className="day-header">{dayToDate(Number(day))}</h4>
          {dayGames.map(g => {
            const isHome = g.home_team_id === teamId;
            const opp = isHome ? g.away : g.home;
            const oppName = opp ? `${opp.city} ${opp.name}` : 'Unknown';
            const isCompleted = g.status === 'completed';
            const teamScore = isHome ? g.home_score : g.away_score;
            const oppScore = isHome ? g.away_score : g.home_score;
            const isWin = g.winner === teamId;
            const isLoss = isCompleted && !isWin;

            if (isCompleted) {
              if (isWin) record.w++;
              else record.l++;
            }

            return (
              <div key={g.id} className={`schedule-game ${isCompleted ? (isWin ? 'win' : 'loss') : 'upcoming'}`}>
                <span className="game-indicator">{isHome ? 'vs' : '@'}</span>
                <span className="opp-name" style={{cursor: 'pointer'}} onClick={() => {
                  const oppId = isHome ? g.away_team_id : g.home_team_id;
                  onNavigate?.('team-' + oppId);
                }}>{oppName}</span>
                {isCompleted ? (
                  <>
                    <span className={`score ${isWin ? 'win' : 'loss'}`} style={{cursor:'pointer', textDecoration:'underline'}} onClick={() => onViewBoxscore?.({gameId: g.game_id || g.id, leagueId: g.league_id || (team as any)?.league_id || ''})}>
                      {isWin ? 'W' : 'L'} {teamScore}-{oppScore}
                    </span>
                    <span className="record-after">({record.w}-{record.l})</span>
                  </>
                ) : (
                  <span className="upcoming-label">—</span>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Team stats tables (hitters + pitchers with real stats)
const TeamStatsTables: React.FC<{ teamId?: string; players: PlayerFromDB[]; onNavigate?: (page: string) => void; leagueId?: string }> = ({ teamId, players, onNavigate, leagueId }) => {
  const [stats, setStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'hitters' | 'pitchers'>('hitters');
  const [sortCol, setSortCol] = useState('_ops');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [seasons, setSeasons] = useState<number[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number | 'current'>('current');

  useEffect(() => { loadSeasons(); }, [teamId]);
  useEffect(() => { loadStats(); }, [teamId, selectedSeason]);

  async function loadSeasons() {
    if (!isSupabaseConfigured() || !supabase || !teamId) return;
    const { data } = await supabase.from('season_history_stats').select('season_year').eq('team_id', teamId);
    if (data) {
      const years = Array.from(new Set(data.map((d: any) => d.season_year))).sort((a: number, b: number) => b - a);
      setSeasons(years);
    }
  }

  async function loadStats() {
    setLoading(true);
    if (!isSupabaseConfigured() || !supabase || !teamId || players.length === 0) { setLoading(false); return; }

    const playerIds = players.map(p => p.id);
    const playerMap = new Map<string, any>();
    players.forEach(p => playerMap.set(p.id, p));

    const allStatData: any[] = [];
    
    // Get current season year from the team's league
    let currentSeasonYear = new Date().getFullYear();
    if (leagueId) {
      const { data: leagueData } = await supabase.from('leagues').select('season_year').eq('id', leagueId).single();
      currentSeasonYear = leagueData?.season_year || new Date().getFullYear();
    }

    if (selectedSeason === 'current') {
      for (let i = 0; i < playerIds.length; i += 100) {
        const batch = playerIds.slice(i, i + 100);
        const { data } = await supabase.from('player_season_stats').select('*').in('player_id', batch).eq('season_year', currentSeasonYear).gt('games', 0);
        if (data) allStatData.push(...data);
      }
    } else {
      // Load from season_history_stats for selected year
      for (let i = 0; i < playerIds.length; i += 100) {
        const batch = playerIds.slice(i, i + 100);
        const { data } = await supabase.from('season_history_stats').select('*').in('player_id', batch).eq('season_year', selectedSeason).gt('games', 0);
        if (data) allStatData.push(...data);
      }
    }

    setStats(allStatData.map(s => ({ ...s, player: playerMap.get(s.player_id) || null })));
    setLoading(false);
  }

  function handleSort(col: string) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir(col === '_era' || col === '_whip' || col === '_bb' ? 'asc' : 'desc'); }
  }

  const SortH: React.FC<{ col: string; children: React.ReactNode }> = ({ col, children }) => (
    <th className="sortable" onClick={() => handleSort(col)}>
      {children} {sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  if (loading) return <p className="loading-text">Loading stats...</p>;

  const isPitcherPos = (pos: string) => {
    const p = (pos || '').toUpperCase();
    return p === 'P';
  };

  const hitters = stats
    .filter(s => s.player && !isPitcherPos(s.player.position))
    .map(s => {
      const ab = s.at_bats || 0, h = s.hits || 0, bb = s.walks || 0;
      const singles = h - (s.doubles || 0) - (s.triples || 0) - (s.home_runs || 0);
      const tb = singles + (s.doubles || 0) * 2 + (s.triples || 0) * 3 + (s.home_runs || 0) * 4;
      const pa = s.plate_appearances || ab + bb;
      const avg = ab > 0 ? h / ab : 0, obp = pa > 0 ? (h + bb) / pa : 0, slg = ab > 0 ? tb / ab : 0;
      return { ...s, _avg: avg, _ops: obp + slg, _h: h, _hr: s.home_runs || 0, _rbi: s.rbi || 0, _r: s.runs || 0, _bb: bb, _k: s.strikeouts || 0, _sb: s.stolen_bases || 0, _ab: ab, _g: s.games || 0 };
    });

  const pitchers = stats
    .filter(s => s.player && isPitcherPos(s.player.position))
    .map(s => {
      const ip = s.innings_pitched || 0, er = s.earned_runs || 0, bb = s.walks_pitching || s.walks_allowed || 0, h = s.hits_allowed || 0;
      return { ...s, _era: ip > 0 ? (er * 9) / ip : 99.99, _ip: ip, _w: s.wins || 0, _l: s.losses || 0, _k: s.strikeouts_pitched || 0, _bb: bb, _h: h, _whip: ip > 0 ? (bb + h) / ip : 0, _g: s.games || 0, _gs: s.starts || 0, _sv: s.saves || 0 };
    });

  const sorted = view === 'hitters'
    ? [...hitters].sort((a: any, b: any) => sortDir === 'asc' ? (a[sortCol] ?? 0) - (b[sortCol] ?? 0) : (b[sortCol] ?? 0) - (a[sortCol] ?? 0))
    : [...pitchers].sort((a: any, b: any) => sortDir === 'asc' ? (a[sortCol] ?? 0) - (b[sortCol] ?? 0) : (b[sortCol] ?? 0) - (a[sortCol] ?? 0));

  return (
    <div style={{ marginTop: 20 }}>
      <div className="leaders-controls">
        <div className="leaders-toggle">
          <button className={`ltab ${view === 'hitters' ? 'active' : ''}`} onClick={() => { setView('hitters'); setSortCol('_ops'); setSortDir('desc'); }}>🏏 Hitters</button>
          <button className={`ltab ${view === 'pitchers' ? 'active' : ''}`} onClick={() => { setView('pitchers'); setSortCol('_era'); setSortDir('asc'); }}>⚾ Pitchers</button>
        </div>
        {seasons.length > 0 && (
          <select className="season-select" value={selectedSeason} onChange={e => setSelectedSeason(e.target.value === 'current' ? 'current' : parseInt(e.target.value))}>
            <option value="current">Current Season</option>
            {seasons.map(y => <option key={y} value={y}>Season {y}</option>)}
          </select>
        )}
      </div>
      <div className="leaders-table-wrap">
        {view === 'hitters' ? (
          <table className="leaders-table">
            <thead><tr>
              <th>#</th><th>Player</th><th>Pos</th>
              <SortH col="_g">G</SortH><SortH col="_ab">AB</SortH><SortH col="_h">H</SortH>
              <SortH col="_hr">HR</SortH><SortH col="_rbi">RBI</SortH><SortH col="_r">R</SortH>
              <SortH col="_bb">BB</SortH><SortH col="_k">K</SortH><SortH col="_sb">SB</SortH>
              <SortH col="_avg">AVG</SortH><SortH col="_ops">OPS</SortH>
            </tr></thead>
            <tbody>
              {sorted.map((s: any, idx: number) => (
                <tr key={s.player_id}>
                  <td className="rank">{idx + 1}</td>
                  <td className="player-name" style={{cursor:'pointer'}} onClick={() => onNavigate?.('/player/' + s.player_id)}>
                    {s.player ? `${s.player.first_name} ${s.player.last_name}` : 'Unknown'}
                  </td>
                  <td>{s.player?.position?.toUpperCase() === 'P' ? 'P' : (s.player?.position || '—')}</td>
                  <td>{s._g}</td><td>{s._ab}</td><td>{s._h}</td>
                  <td>{s._hr}</td><td>{s._rbi}</td><td>{s._r}</td>
                  <td>{s._bb}</td><td>{s._k}</td><td>{s._sb}</td>
                  <td className="stat-highlight">{s._avg.toFixed(3).replace(/^0/, '')}</td>
                  <td className="stat-highlight">{s._ops.toFixed(3).replace(/^0/, '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="leaders-table">
            <thead><tr>
              <th>#</th><th>Player</th>
              <SortH col="_g">G</SortH><SortH col="_gs">GS</SortH><SortH col="_w">W</SortH><SortH col="_l">L</SortH>
              <SortH col="_sv">SV</SortH><SortH col="_era">ERA</SortH><SortH col="_ip">IP</SortH>
              <SortH col="_h">H</SortH><SortH col="_bb">BB</SortH><SortH col="_k">K</SortH>
              <SortH col="_whip">WHIP</SortH>
            </tr></thead>
            <tbody>
              {sorted.map((s: any, idx: number) => (
                <tr key={s.player_id}>
                  <td className="rank">{idx + 1}</td>
                  <td className="player-name" style={{cursor:'pointer'}} onClick={() => onNavigate?.('/player/' + s.player_id)}>
                    {s.player ? `${s.player.first_name} ${s.player.last_name}` : 'Unknown'}
                  </td>
                  <td>{s._g}</td><td>{s._gs || 0}</td><td>{s._w}</td><td>{s._l}</td>
                  <td>{s._sv}</td>
                  <td className="stat-highlight">{s._era.toFixed(2)}</td>
                  <td>{s._ip.toFixed(1)}</td>
                  <td>{s._h}</td><td>{s._bb}</td><td>{s._k}</td>
                  <td>{s._whip.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {sorted.length === 0 && <p className="no-data">No stats yet. Stats appear after games are simulated.</p>}
      </div>
    </div>
  );
};

export default TeamDetailPage;

// View-only draft priority component for team page
function DraftPriorityView({ leagueId, teamId, onNavigate }: { leagueId: string; teamId: string; onNavigate?: (r: string) => void }) {
  const [priorities, setPriorities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('draft_priority_sheets')
        .select('player_id, priority_rank, players:player_id(first_name, last_name, position, overall)')
        .eq('team_id', teamId)
        .eq('league_id', leagueId)
        .order('priority_rank', { ascending: true });
      setPriorities(data || []);
      setLoading(false);
    };
    load();
  }, [leagueId, teamId]);

  if (loading) return <div style={{padding:20,color:'#666',textAlign:'center'}}>Loading draft sheet...</div>;
  if (priorities.length === 0) return <div style={{padding:20,color:'#666',textAlign:'center'}}>No draft priority set yet.</div>;

  return (
    <div>
      <h3 style={{color:'#fff',marginBottom:12}}>Your Draft Priority (View Only)</h3>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.85rem'}}>
        <thead><tr>
          <th style={{color:'#9ca3af',padding:6,textAlign:'left'}}>Rank</th>
          <th style={{color:'#9ca3af',padding:6,textAlign:'left'}}>Player</th>
          <th style={{color:'#9ca3af',padding:6,textAlign:'center'}}>Pos</th>
          <th style={{color:'#9ca3af',padding:6,textAlign:'center'}}>OVR</th>
        </tr></thead>
        <tbody>
          {priorities.map((p, i) => {
            const pl = Array.isArray(p.players) ? p.players[0] : p.players;
            return (
              <tr key={p.player_id} style={{borderBottom:'1px solid #1e293b'}}>
                <td style={{padding:6,color:'#9ca3af'}}>{p.priority_rank}</td>
                <td style={{padding:6,color:'#fff'}}>{pl?.first_name} {pl?.last_name}</td>
                <td style={{padding:6,color:'#9ca3af',textAlign:'center'}}>{pl?.position}</td>
                <td style={{padding:6,color:pl?.overall>=80?'#f59e0b':'#9ca3af',textAlign:'center',fontWeight:600}}>{pl?.overall ? numberToGrade(pl.overall) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{marginTop:12}}>
        <button onClick={() => onNavigate?.(`/league/${leagueId}/draft-sheet`)}
          style={{padding:'8px 16px',borderRadius:6,border:'none',background:'#3b82f6',color:'#fff',cursor:'pointer',fontSize:'0.9rem'}}>
          ✏️ Edit Draft Sheet
        </button>
      </div>
    </div>
  );
}

// Mon Apr 13 22:21:10 UTC 2026

// ============================================
// Lineup & Strategy Tab
// ============================================
interface StrategySettings {
  stealAttempts: number;
  sacrificeBunts: number;
  hittingApproach: number;
  shiftFrequency: number;
  intentionalWalks: number;
  pitcherManagement: number;
  baseRunningAggression: number;
}

const DEFAULT_STRATEGY: StrategySettings = {
  stealAttempts: 50, sacrificeBunts: 50, hittingApproach: 50,
  shiftFrequency: 50, intentionalWalks: 50, pitcherManagement: 50, baseRunningAggression: 50,
};

const STRATEGY_SLIDERS: { group: string; items: { key: keyof StrategySettings; label: string; left: string; right: string }[] }[] = [
  { group: '⚔️ Offense', items: [
    { key: 'hittingApproach', label: 'Hitting Approach', left: 'Contact', right: 'Power' },
    { key: 'stealAttempts', label: 'Steal Attempts', left: 'Conservative', right: 'Aggressive' },
    { key: 'sacrificeBunts', label: 'Sacrifice Bunts', left: 'Never', right: 'Often' },
    { key: 'baseRunningAggression', label: 'Baserunning', left: 'Safe', right: 'Risky' },
  ]},
  { group: '🛡️ Defense', items: [
    { key: 'pitcherManagement', label: 'Pitcher Management', left: 'Leave In', right: 'Quick Hook' },
    { key: 'intentionalWalks', label: 'Intentional Walks', left: 'Never', right: 'Often' },
    { key: 'shiftFrequency', label: 'Defensive Shifts', left: 'Standard', right: 'Shift' },
  ]},
];

const FIELD_POSITIONS = ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] as const;
const BULLPEN_ROLE_LABELS: Record<string, string> = {
  P1: 'SP1', P2: 'SP2', P3: 'SP3', P4: 'SP4', P5: 'SP5',
  P6: 'MR', P7: 'RP1', P8: 'RP2', P9: 'SU1', P10: 'SU2', P11: 'CL',
};

type FieldSlot = { player: any; position: string; tired: boolean; backup: boolean };

const LineupStrategyTab: React.FC<{ teamId: string; team: any; players: any[]; currentUserId?: string }> = ({ teamId, team, players, currentUserId }) => {
  const isOwner = currentUserId && team?.owner_id === currentUserId;
  const [strategy, setStrategy] = useState<StrategySettings>(team?.strategy || DEFAULT_STRATEGY);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const playerMap = new Map(players.map(p => [p.id, p]));
  const restThresholdPct = ((team as any)?.rest_threshold ?? 50) / 100;

  const isFresh = (p: any) => {
    const max = p.endurance ?? 100;
    if (max <= 0) return true;
    return ((p.current_stamina ?? 100) / max) >= restThresholdPct;
  };

  // ── Build field lineup from depth chart ──
  const fieldLineup = (() => {
    const dc = (team as any)?.depth_chart;
    if (!dc || typeof dc !== 'object' || Array.isArray(dc)) {
      // No depth chart: fall back to top 9 hitters, show natural position
      const hitters = players.filter((p: any) => p.is_hitter).sort((a: any, b: any) => (b.overall || 50) - (a.overall || 50));
      return hitters.slice(0, 9).map((p: any) => ({
        player: p, position: p.position, tired: false, backup: false,
      } as FieldSlot));
    }

    const usedIds = new Set<string>();
    const slots: FieldSlot[] = [];

    // Resolve each position from depth chart, stamina-aware
    for (const pos of FIELD_POSITIONS) {
      const chain: string[] | undefined = dc[pos];
      if (!chain || !Array.isArray(chain)) continue;

      let chosen: FieldSlot | null = null;
      for (const pid of chain) {
        const p = playerMap.get(pid);
        if (!p || p.retired || !p.is_hitter) continue;
        if (usedIds.has(pid)) continue;

        if (isFresh(p)) {
          chosen = { player: p, position: pos, tired: false, backup: false };
          break;
        }
        if (!chosen) {
          chosen = { player: p, position: pos, tired: true, backup: false };
        }
      }

      if (chosen) {
        usedIds.add(chosen.player.id);
        slots.push(chosen);
      }
    }

    // Fill DH slot(s) with best remaining hitters (up to 9 total)
    if (slots.length < 9) {
      const remaining = players
        .filter((p: any) => p.is_hitter && !usedIds.has(p.id) && !p.retired)
        .sort((a: any, b: any) => (b.overall || 50) - (a.overall || 50));
      for (const p of remaining) {
        if (slots.length >= 9) break;
        usedIds.add(p.id);
        slots.push({ player: p, position: 'DH', tired: !isFresh(p), backup: false });
      }
    }

    return slots.slice(0, 9);
  })();

  // ── Build pitching staff from depth chart P1-P11 ──
  const pitchingStaff = (() => {
    const dc = (team as any)?.depth_chart;
    if (!dc || typeof dc !== 'object' || Array.isArray(dc)) {
      const arms = players.filter((p: any) => !p.is_hitter).sort((a: any, b: any) => (b.overall || 50) - (a.overall || 50));
      return arms.slice(0, 11).map((p: any, i: number) => ({ player: p, role: BULLPEN_ROLE_LABELS[`P${i + 1}`] || `P${i + 1}` }));
    }

    const staff: { player: any; role: string }[] = [];
    for (let i = 1; i <= 11; i++) {
      const key = `P${i}`;
      const chain: string[] | undefined = dc[key];
      if (!chain || !Array.isArray(chain) || chain.length === 0) continue;
      const pid = chain[0];
      const p = playerMap.get(pid);
      if (p && !p.retired) {
        staff.push({ player: p, role: BULLPEN_ROLE_LABELS[key] || key });
      }
    }
    return staff;
  })();

  const updateSlider = (key: keyof StrategySettings, val: number) => {
    setStrategy(prev => ({ ...prev, [key]: val }));
    setSaved(false);
  };

  const saveStrategy = async () => {
    setSaving(true);
    const { error } = await supabase.from('teams').update({ strategy }).eq('id', teamId);
    setSaving(false);
    if (!error) setSaved(true);
  };

  return (
    <div className="tab-content">
      {/* Batting Order */}
      <h3>📋 Batting Order</h3>
      <p style={{color:'#888',fontSize:13,marginBottom:8}}>
        Auto-built from depth chart. Tired players shown in red.
      </p>
      <div style={{display:'flex',flexDirection:'column',gap:4,marginBottom:24}}>
        {fieldLineup.length === 0 && (
          <p style={{color:'#666',fontStyle:'italic'}}>No depth chart configured. Build one from the Roster tab.</p>
        )}
        {fieldLineup.map((slot, idx) => {
          const { player: p } = slot;
          const staminaPct = p.endurance ? Math.round((p.current_stamina ?? 100) / p.endurance * 100) : 100;
          const posColor = slot.tired ? '#ef4444' : '#888';
          return (
            <div key={p.id} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'#1a1a2e',borderRadius:6}}>
              <span style={{color:'#fbbf24',fontWeight:700,width:24,textAlign:'center'}}>{idx + 1}</span>
              <span style={{flex:1}}>{p.first_name} {p.last_name}</span>
              <span style={{color:posColor,fontSize:12,fontWeight:600,minWidth:28,textAlign:'center'}}>{slot.position}</span>
              <span style={{color:'#94a3b8',fontSize:11,minWidth:36,textAlign:'right'}}>{staminaPct}%</span>
              <span style={{color:'#4ade80',fontWeight:600,width:30,textAlign:'center'}}>{numberToGrade(p.overall)}</span>
            </div>
          );
        })}
      </div>

      {/* Pitching Staff */}
      <h3>⚾ Pitching Staff</h3>
      <p style={{color:'#888',fontSize:13,marginBottom:8}}>
        Built from depth chart slots P1–P11. SP1 marked with <span style={{color:'#fbbf24'}}>*</span> as projected starter.
      </p>
      <div style={{display:'flex',flexDirection:'column',gap:4,marginBottom:24}}>
        {pitchingStaff.length === 0 && (
          <p style={{color:'#666',fontStyle:'italic'}}>No pitching depth chart configured.</p>
        )}
        {pitchingStaff.map((slot) => {
          const { player: p, role } = slot;
          const isSP1 = role === 'SP1';
          const roleColor = role.startsWith('SP') ? '#60a5fa' : role === 'CL' ? '#fbbf24' : '#a78bfa';
          return (
            <div key={p.id} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'#1a1a2e',borderRadius:6}}>
              <span style={{color:roleColor,fontWeight:700,minWidth:36,textAlign:'center',fontSize:13}}>
                {role}{isSP1 && <span style={{color:'#fbbf24'}}>*</span>}
              </span>
              <span style={{flex:1}}>{p.first_name} {p.last_name}</span>
              <span style={{color:'#4ade80',fontWeight:600,width:30,textAlign:'center'}}>{numberToGrade(p.overall)}</span>
            </div>
          );
        })}
      </div>

      {/* Strategy Sliders — owner only */}
      {isOwner ? (
      <>
      <h3>🎛️ Strategy Sliders</h3>
      <p style={{color:'#888',fontSize:13,marginBottom:12}}>These directly affect how your team plays in every simulated game.</p>
      {STRATEGY_SLIDERS.map(group => (
        <div key={group.group} style={{marginBottom:20}}>
          <h4 style={{color:'#e2e8f0',marginBottom:8}}>{group.group}</h4>
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            {group.items.map(s => (
              <div key={s.key}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                  <span style={{fontSize:13,color:'#94a3b8'}}>{s.left}</span>
                  <span style={{fontSize:13,fontWeight:600,color:'#e2e8f0'}}>{s.label}: {strategy[s.key]}</span>
                  <span style={{fontSize:13,color:'#94a3b8'}}>{s.right}</span>
                </div>
                <input
                  type="range" min={0} max={100}
                  value={strategy[s.key]}
                  onChange={e => updateSlider(s.key, parseInt(e.target.value))}
                  style={{width:'100%',accentColor:'#4ade80'}}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      <button
        onClick={saveStrategy}
        disabled={saving}
        style={{padding:'10px 24px',background:saved ? '#059669' : '#2563eb',color:'#fff',border:'none',borderRadius:8,fontWeight:600,fontSize:14,cursor:saving?'wait':'pointer'}}
      >
        {saving ? 'Saving...' : saved ? '✅ Saved!' : '💾 Save Strategy'}
      </button>
      </>
      ) : (
        <div style={{marginTop: 24, padding: 16, background: '#1a1a2e', borderRadius: 8, textAlign: 'center', color: '#6b7280', fontSize: 13}}>
          🔒 Strategy settings are only visible to the team owner.
        </div>
      )}
    </div>
  );
};

// ============================================
// Free Agency Tab (inline in team page)
// ============================================
const FreeAgencyTab: React.FC<{ leagueId: string; teamId: string; currentUserId?: string | null }> = ({ leagueId, teamId, currentUserId }) => {
  const [faPlayers, setFaPlayers] = useState<any[]>([]);
  const [roster, setRoster] = useState<any[]>([]);
  const [budget, setBudget] = useState(1000000);
  const [myBids, setMyBids] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'fa' | 'roster'>('fa');
  const [bidModal, setBidModal] = useState<any>(null);
  const [bidAmount, setBidAmount] = useState(0);
  const [waiveId, setWaiveId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<{type:string;text:string}|null>(null);
  const [posFilter, setPosFilter] = useState('ALL');

  useEffect(() => { loadData(); }, [leagueId, teamId]);

  const loadData = async () => {
    const [faRes, rosterRes, budgetRes, bidsRes] = await Promise.all([
      supabase.from('players').select('*').eq('league_id', leagueId).is('team_id', null).eq('is_scrub', false).order('overall', { ascending: false }),
      supabase.from('players').select('*').eq('team_id', teamId).order('position'),
      supabase.from('teams').select('fa_budget').eq('id', teamId).single(),
      supabase.from('fa_bids').select('*').eq('league_id', leagueId).eq('team_id', teamId).eq('status', 'pending'),
    ]);
    if (faRes.data) setFaPlayers(faRes.data);
    if (rosterRes.data) setRoster(rosterRes.data);
    if (budgetRes.data) setBudget(budgetRes.data.fa_budget || 1000000);
    if (bidsRes.data) setMyBids(bidsRes.data);
  };

  const submitBid = async () => {
    if (!bidModal || !waiveId) return;
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setMsg({type:'error',text:'Not authenticated'}); setSubmitting(false); return; }
      const r = await fetch(`${FUNCTIONS_URL}/fa-bid-v2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ leagueId, faPlayerId: bidModal.id, salaryOffer: 0, waivedPlayerId: waiveId }),
      });
      const data = await r.json();
      if (data.error) { setMsg({type:'error',text:data.error}); }
      else { setMsg({type:'success',text:`✅ Bid submitted for ${data.faPlayer || 'player'}!`}); setBidModal(null); setWaiveId(''); loadData(); }
    } catch (e: any) { setMsg({type:'error',text:e.message}); }
    setSubmitting(false);
  };

  const filteredFa = faPlayers.filter(p => {
    if (posFilter !== 'ALL') {
      if (posFilter === 'P') return p.position === 'P';
      return p.position === posFilter;
    }
    return true;
  });

  const hitters = roster.filter(p => p.is_hitter !== false);
  const pitchers = roster.filter(p => p.is_hitter === false);
  const posCounts: Record<string, number> = {};
  roster.forEach(p => { posCounts[p.position] = (posCounts[p.position] || 0) + 1; });

  return (
    <div className="tab-content">
      {/* Info bar */}
      <div style={{display:'flex',gap:16,padding:'8px 0',marginBottom:8,borderBottom:'1px solid #1e293b',flexWrap:'wrap'}}>
        <span style={{color:'#93c5fd',fontSize:'0.85rem'}}>💵 Budget: <b style={{color:'#fff'}}>${budget.toLocaleString()}</b></span>
        <span style={{color:'#93c5fd',fontSize:'0.85rem'}}>📋 Bids: <b style={{color:'#fff'}}>{myBids.length}</b></span>
        <span style={{color:'#93c5fd',fontSize:'0.85rem'}}>👥 Roster: <b style={{color:'#fff'}}>{roster.length}/25</b></span>
      </div>

      {msg && <div style={{padding:10,marginBottom:12,borderRadius:6,textAlign:'center',background:msg.type==='error'?'rgba(239,68,68,0.15)':'rgba(34,197,94,0.15)',color:msg.type==='error'?'#fca5a5':'#86efac',fontSize:'0.85rem'}}>{msg.text}</div>}

      {/* Toggle */}
      <div style={{display:'flex',gap:0,marginBottom:12}}>
        <button onClick={() => setViewMode('fa')} style={{flex:1,padding:'8px',border:'none',cursor:'pointer',background:viewMode==='fa'?'#2563eb':'#1e293b',color:viewMode==='fa'?'#fff':'#94a3b8',borderRadius:'8px 0 0 8px',fontWeight:600,fontSize:'0.85rem'}}>🤝 Free Agents ({faPlayers.length})</button>
        <button onClick={() => setViewMode('roster')} style={{flex:1,padding:'8px',border:'none',cursor:'pointer',background:viewMode==='roster'?'#2563eb':'#1e293b',color:viewMode==='roster'?'#fff':'#94a3b8',borderRadius:'0 8px 8px 0',fontWeight:600,fontSize:'0.85rem'}}>📋 My Roster ({roster.length}/25)</button>
      </div>

      {/* FA View */}
      {viewMode === 'fa' && (<>
        {/* Position filters */}
        <div style={{display:'flex',gap:4,marginBottom:12,flexWrap:'wrap'}}>
          {['ALL','P','C','1B','2B','3B','SS','OF'].map(pos => (
            <button key={pos} onClick={() => setPosFilter(pos)} style={{padding:'4px 10px',borderRadius:12,border:`1px solid ${posFilter===pos?'#3b82f6':'#374151'}`,background:posFilter===pos?'#1e40af':'transparent',color:posFilter===pos?'#fff':'#9ca3af',cursor:'pointer',fontSize:'0.75rem'}}>{pos}</button>
          ))}
        </div>

        {/* Active bids */}
        {myBids.length > 0 && (
          <div style={{marginBottom:16}}>
            <h4 style={{color:'#fbbf24',marginBottom:8,fontSize:'0.9rem'}}>📋 Your Pending Bids</h4>
            {myBids.map(bid => {
              const faP = faPlayers.find(p => p.id === bid.fa_player_id);
              const waP = roster.find(p => p.id === bid.waive_player_id);
              return (
                <div key={bid.id} style={{padding:'6px 10px',background:'rgba(59,130,246,0.08)',borderRadius:6,marginBottom:4,fontSize:'0.8rem',color:'#93c5fd'}}>
                  🤝 {faP ? `${faP.first_name} ${faP.last_name}` : bid.fa_player_id?.slice(0,8)} · Waive: {waP ? `${waP.first_name} ${waP.last_name}` : '?'}
                </div>
              );
            })}
          </div>
        )}

        {/* FA player grid */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:8}}>
          {filteredFa.map(p => (
            <div key={p.id} style={{background:'#1a1a2e',borderRadius:8,padding:10,cursor:'pointer',border:'1px solid #2d2d4a'}} onClick={() => { setBidModal(p); setBidAmount(0); setWaiveId(''); }}>
              <div style={{fontWeight:600,color:'#e2e8f0',fontSize:'0.85rem'}}>{p.first_name} {p.last_name}</div>
              <div style={{color:'#9ca3af',fontSize:'0.75rem'}}>{p.position} · OVR {numberToGrade(p.overall)}</div>
              <div style={{color:'#4ade80',fontSize:'0.7rem',marginTop:4}}>Make Offer →</div>
            </div>
          ))}
        </div>
        {filteredFa.length === 0 && <p style={{color:'#666',textAlign:'center',padding:20}}>No free agents available</p>}
      </>)}

      {/* Roster View */}
      {viewMode === 'roster' && (
        <div>
          {/* Roster holes */}
          {(() => {
            const needed: Record<string,number> = {C:2,'1B':1,'2B':1,'3B':1,SS:1,OF:5,UTIL:3,P:10};
            const holes: string[] = [];
            Object.entries(needed).forEach(([pos,need]) => { const have = posCounts[pos]||0; if(have<need) holes.push(`${pos}: ${have}/${need}`); });
            if (roster.length < 25) holes.push(`Total: ${roster.length}/25`);
            return holes.length ? (
              <div style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:8,padding:12,marginBottom:16,fontSize:'0.85rem',color:'#fca5a5'}}>
                ⚠️ <b>Roster Holes:</b> {holes.join(' · ')}
              </div>
            ) : (
              <div style={{background:'rgba(34,197,94,0.1)',border:'1px solid rgba(34,197,94,0.3)',borderRadius:8,padding:12,marginBottom:16,fontSize:'0.85rem',color:'#86efac'}}>
                ✅ <b>Roster Full</b> — All positions covered
              </div>
            );
          })()}

          <h4 style={{color:'#fbbf24',marginBottom:8}}>🏏 Hitters ({hitters.length})</h4>
          <div style={{display:'flex',flexDirection:'column',gap:4,marginBottom:16}}>
            {hitters.sort((a,b)=>(b.overall||50)-(a.overall||50)).map(p => (
              <div key={p.id} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'#1a1a2e',borderRadius:6}}>
                <span style={{color:'#94a3b8',width:32,fontSize:'0.8rem'}}>{p.position}</span>
                <span style={{flex:1,color:'#e2e8f0'}}>{p.first_name} {p.last_name}</span>
                <span style={{color:'#4ade80',fontWeight:600,width:30,textAlign:'center'}}>{numberToGrade(p.overall)}</span>
              </div>
            ))}
          </div>

          <h4 style={{color:'#60a5fa',marginBottom:8}}>⚾ Pitchers ({pitchers.length})</h4>
          <div style={{display:'flex',flexDirection:'column',gap:4,marginBottom:16}}>
            {pitchers.sort((a,b)=>(b.overall||50)-(a.overall||50)).map(p => (
              <div key={p.id} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'#1a1a2e',borderRadius:6}}>
                <span style={{color:'#94a3b8',width:32,fontSize:'0.8rem'}}>{p.position}</span>
                <span style={{flex:1,color:'#e2e8f0'}}>{p.first_name} {p.last_name}</span>
                <span style={{color:'#4ade80',fontWeight:600,width:30,textAlign:'center'}}>{numberToGrade(p.overall)}</span>
              </div>
            ))}
          </div>

          <button onClick={() => setViewMode('fa')} style={{padding:'10px 20px',background:'#2563eb',color:'#fff',border:'none',borderRadius:8,fontWeight:600,cursor:'pointer'}}>🤝 Back to Free Agents</button>
        </div>
      )}

      {/* Bid Modal */}
      {bidModal && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200,padding:16}} onClick={() => setBidModal(null)}>
          <div style={{background:'#1e293b',borderRadius:16,padding:24,maxWidth:420,width:'100%',border:'1px solid rgba(255,255,255,0.1)'}} onClick={e=>e.stopPropagation()}>
            <h3 style={{marginBottom:16,color:'#fff'}}>🤝 Sign Player</h3>
            <div style={{background:'rgba(59,130,246,0.1)',borderRadius:8,padding:12,marginBottom:16}}>
              <div style={{fontWeight:600,color:'#fff'}}>{bidModal.first_name} {bidModal.last_name}</div>
              <div style={{color:'#9ca3af',fontSize:'0.85rem'}}>{bidModal.position} | OVR {numberToGrade(bidModal.overall)}</div>
            </div>
            <div style={{marginBottom:16}}>
              <p style={{color:'#9ca3af',fontSize:'0.85rem',marginBottom:12}}>Sign this player to your team — no contracts, no salary. Just baseball.</p>
            </div>
            <div style={{marginBottom:16}}>
              <label style={{color:'#9ca3af',fontSize:'0.85rem',display:'block',marginBottom:6}}>🔻 Player to Waive (sign one → waive one)</label>
              <select value={waiveId} onChange={e=>setWaiveId(e.target.value)} style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid #374151',background:'#0f172a',color:'#fff',fontSize:'0.9rem'}}>
                <option value="">— Select player to waive —</option>
                {roster.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name} | {p.position} | OVR {numberToGrade(p.overall)}</option>)}
              </select>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button onClick={submitBid} disabled={submitting||!waiveId} style={{flex:1,padding:'10px',background:'#2563eb',color:'#fff',border:'none',borderRadius:8,fontWeight:600,cursor:submitting?'wait':'pointer',opacity:(submitting||!waiveId)?0.5:1}}>
                {submitting ? 'Submitting...' : 'Sign Player'}
              </button>
              <button onClick={()=>setBidModal(null)} style={{padding:'10px 16px',background:'#374151',color:'#fff',border:'none',borderRadius:8,cursor:'pointer'}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

